import {
	faCheck,
	faMagnifyingGlass,
	faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { ListGroup, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ModalAtom, TextFieldAtom } from "../../components/atoms/atom";
import { useListUsersQuery } from "../../store/services/adminUsers";
import { useAssignProjectAdminMutation } from "../../store/services/projectMembers";
import { showToast } from "../../utils/method";

function AssignProjectAdminModal({
	show,
	project,
	onClose,
	onAssigned,
}) {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [selectedUserId, setSelectedUserId] = useState(null);

	const { data, isLoading, isFetching } = useListUsersQuery("global", {
		skip: !show,
	});
	const [assignAdmin, { isLoading: assigning }] =
		useAssignProjectAdminMutation();

	// Sort once on first name → last name → email (case- and diacritic-insensitive
	// so "Ülker" sorts next to "Ulker" instead of after "Z"). Users without a name
	// fall back to email so they still get a stable, predictable position.
	const globalUsers = useMemo(() => {
		const items = data?.items ?? [];
		const sortKey = (u) =>
			[u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
			u.email ||
			"";
		return [...items].sort((a, b) =>
			sortKey(a).localeCompare(sortKey(b), undefined, {
				sensitivity: "base",
				numeric: true,
			}),
		);
	}, [data?.items]);

	const filteredUsers = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return globalUsers;
		return globalUsers.filter((u) => {
			const haystack = [
				u.firstName,
				u.lastName,
				u.email,
				u.role?.name,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			return haystack.includes(term);
		});
	}, [globalUsers, search]);

	const handleClose = () => {
		setSearch("");
		setSelectedUserId(null);
		onClose();
	};

	const handleSubmit = async () => {
		if (!project?.id || !selectedUserId) {
			showToast(t("projects.assign-admin.select-user"), {
				type: "error",
			});
			return;
		}
		try {
			const result = await assignAdmin({
				projectId: project.id,
				userId: selectedUserId,
			}).unwrap();
			showToast(t("projects.assign-admin.success"), {
				type: "success",
			});
			onAssigned?.(result, project.id);
			handleClose();
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const listLoading = isLoading || isFetching;

	return (
		<ModalAtom
			show={show}
			handleClose={handleClose}
			title={t("projects.assign-admin.title")}
			titleHelpText={t("projects.assign-admin.help")}
			saveText={
				assigning ? "common.saving" : "projects.assign-admin.confirm"
			}
			disabled={assigning}
			submitDisabled={!selectedUserId}
			handleSubmit={handleSubmit}
			size="lg"
			body={
				<div className="assign-project-admin-modal">
					{project?.name && (
						<p className="text-muted mb-3">{project.name}</p>
					)}
					<TextFieldAtom
						type="search"
						placeholder={t("filter-bar.search")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						suffix={
							<FontAwesomeIcon
								icon={faMagnifyingGlass}
								style={{ color: "#94a3b8", fontSize: "12px" }}
							/>
						}
					/>
					<div className="assign-project-admin-modal__list mt-3">
						{listLoading ? (
							<div className="text-center py-4">
								<Spinner animation="border" size="sm" />{" "}
								{t("common.loading")}
							</div>
						) : filteredUsers.length === 0 ? (
							<p className="mb-0 text-muted">
								{t("projects.assign-admin.no-users")}
							</p>
						) : (
							<ListGroup>
								{filteredUsers.map((u) => {
									const label = [u.firstName, u.lastName]
										.filter(Boolean)
										.join(" ");
									const selected = selectedUserId === u.id;
									return (
										<ListGroup.Item
											key={u.id}
											action
											onClick={() =>
												setSelectedUserId(u.id)
											}
											className={`assign-project-admin-modal__item${selected ? " assign-project-admin-modal__item--selected" : ""}`}
											aria-pressed={selected}
										>
											<div className="assign-project-admin-modal__item-main">
												<FontAwesomeIcon
													icon={faUserShield}
													className="assign-project-admin-modal__icon"
												/>
												<div className="assign-project-admin-modal__item-text">
													<div className="assign-project-admin-modal__item-name">
														{label || u.email}
													</div>
													{label && (
														<div className="assign-project-admin-modal__item-email">
															{u.email}
														</div>
													)}
												</div>
											</div>
											{selected && (
												<span
													className="assign-project-admin-modal__check"
													aria-hidden="true"
												>
													<FontAwesomeIcon
														icon={faCheck}
													/>
												</span>
											)}
										</ListGroup.Item>
									);
								})}
							</ListGroup>
						)}
					</div>
				</div>
			}
		/>
	);
}

export default AssignProjectAdminModal;
